import type { CommandPaletteEntry } from '../core/command-palette.logic';
import { pageCommandList } from '../shared/page-command-list';

/** Handlers the user menu already owns. */
export interface AccountPaletteContext {
    profile: () => void;
    settings: () => void;
    importData: () => void;
    changePassword: () => void;
    loginHistory: () => void;
    signOut: () => void;
}

/** Ctrl+K commands mirroring the user menu, with the same icons. */
export function buildAccountPaletteCommands(page: AccountPaletteContext): CommandPaletteEntry[] {
    return pageCommandList('app-account', 'Account', [
        {
            id: 'profile',
            title: 'Profile',
            section: 'Global Actions',
            icon: 'heroicons_outline:user-circle',
            route: '/user-profile',
            run: page.profile,
        },
        {
            id: 'settings',
            title: 'Settings',
            section: 'Global Actions',
            icon: 'heroicons_outline:cog-8-tooth',
            route: '/settings',
            run: page.settings,
        },
        {
            id: 'import-data',
            title: 'Import Data',
            section: 'Global Actions',
            icon: 'heroicons_outline:arrow-down-tray',
            run: page.importData,
        },
        {
            id: 'change-password',
            title: 'Change Password',
            section: 'Global Actions',
            icon: 'heroicons_outline:key',
            run: page.changePassword,
        },
        {
            id: 'login-history',
            title: 'Login History',
            section: 'Global Actions',
            icon: 'heroicons_outline:shield-check',
            route: '/access-control/login-history',
            run: page.loginHistory,
        },
        {
            id: 'sign-out',
            title: 'Sign out',
            section: 'Global Actions',
            icon: 'heroicons_outline:arrow-right-on-rectangle',
            run: page.signOut,
        },
    ]);
}

export interface NotificationsPaletteContext {
    unreadCount: number;
    open: () => void;
}

/** Ctrl+K command for the toolbar notifications panel. */
export function buildNotificationsPaletteCommands(
    page: NotificationsPaletteContext,
): CommandPaletteEntry[] {
    return pageCommandList('app-notifications', 'App', [
        {
            id: 'notifications',
            title: 'Notifications',
            section: 'Global Actions',
            icon: 'heroicons_outline:bell',
            hint: page.unreadCount > 0 ? `${page.unreadCount} unread` : undefined,
            run: page.open,
        },
    ]);
}

export interface ShellPaletteContext {
    /** Same visibility rule as the toolbar Global Search button. */
    canGlobalSearch: boolean;
    globalSearch: () => void;
    /** False when the toolbar fullscreen control is not rendered or the browser blocks it. */
    canFullscreen: boolean;
    isFullscreen: boolean;
    toggleFullscreen: () => void;
}

/** Ctrl+K commands for the layout toolbar: Global Search and fullscreen. */
export function buildShellPaletteCommands(page: ShellPaletteContext): CommandPaletteEntry[] {
    return pageCommandList('app-shell', 'App', [
        ...(page.canGlobalSearch
            ? [
                  {
                      id: 'global-search',
                      title: 'Global Search',
                      section: 'Global Actions' as const,
                      icon: 'heroicons_outline:magnifying-glass',
                      route: '/global-search',
                      run: page.globalSearch,
                  },
              ]
            : []),
        {
            id: 'toggle-fullscreen',
            title: 'Toggle Fullscreen',
            section: 'Global Actions',
            icon: 'heroicons_outline:arrows-pointing-out',
            hint: page.isFullscreen ? 'Exit' : 'Enter',
            disabled: !page.canFullscreen,
            run: page.toggleFullscreen,
        },
    ]);
}
